import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { CalendarEvent } from '../../../shared/api.js';
import { api } from '../lib/api';

const HIDDEN_KEY = 'cybernika.calendars.hidden';

/** Calendars the viewer switched off on the overview; kept in this browser only. */
export function useHiddenCalendars(): [ReadonlySet<string>, (id: string) => void] {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => {
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]');
      return new Set(Array.isArray(stored) ? stored.filter((v) => typeof v === 'string') : []);
    } catch {
      return new Set();
    }
  });
  const toggle = (id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]));
      } catch {
        /* storage unavailable: the filter lives only for this page */
      }
      return next;
    });
  };
  return [hidden, toggle];
}

function formatDay(isoDate: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date(`${isoDate}T12:00:00Z`));
}

/** YYYY-MM-DD of `date` in `timeZone`. */
function dayKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(date);
}

function nextDay(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function timeOf(iso: string): string {
  return iso.length > 10 ? iso.slice(11, 16) : '';
}

export function CalendarsCard({
  hidden,
  onToggle,
}: {
  hidden: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['calendars'], queryFn: () => api.calendars() });
  const refresh = async () => {
    const fresh = await api.calendars(true);
    queryClient.setQueryData(['calendars'], fresh);
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  return (
    <article className="card card--wide">
      <header className="card__header">
        <h3>Календари</h3>
        <button type="button" className="btn btn--ghost btn--small" onClick={() => void refresh()}>
          Обновить
        </button>
      </header>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && query.data.calendars.length === 0 && (
        <p className="muted">
          Календарей нет. Поделитесь календарями с Apple ID агента и примите приглашения.
        </p>
      )}
      {query.data && query.data.calendars.length > 0 && (
        <ul className="calendar-list">
          {query.data.calendars.map((c) => (
            <li key={c.id}>
              <label className="calendar-list__toggle" title="Показывать в «Сегодня и завтра»">
                <input
                  type="checkbox"
                  checked={!hidden.has(c.id)}
                  onChange={() => onToggle(c.id)}
                />
                <span
                  className="swatch"
                  style={{ background: c.color ?? 'var(--border)' }}
                  aria-hidden
                />
              </label>
              <div className="calendar-list__main">
                <div className={`calendar-list__name${hidden.has(c.id) ? ' muted' : ''}`}>
                  {c.name}
                  <span className={`pill pill--${c.writable ? 'ok' : 'muted'}`}>
                    {c.writable ? 'запись' : c.readOnlyShared ? 'только чтение' : 'чтение'}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** Today and tomorrow in the calendar's time zone. */
export function UpcomingCard({ tz, hidden }: { tz: string; hidden: ReadonlySet<string> }) {
  const today = dayKey(new Date(), tz);
  const tomorrow = nextDay(today);
  const query = useQuery({
    queryKey: ['events', 'today-tomorrow', today],
    queryFn: () => api.calendarEvents({ start: today, end: tomorrow, limit: 200 }),
    refetchInterval: 120_000,
  });

  // An event that began earlier (a multi-day one) belongs to today.
  const byDay = new Map<string, CalendarEvent[]>([
    [today, []],
    [tomorrow, []],
  ]);
  const shown = (query.data?.events ?? []).filter((e) => !hidden.has(e.calendarId));
  for (const event of shown) {
    const day = event.start.slice(0, 10) < today ? today : event.start.slice(0, 10);
    byDay.get(day)?.push(event);
  }

  return (
    <article className="card card--wide">
      <header className="card__header">
        <h3>Сегодня и завтра</h3>
        {query.data && (
          <span className="muted small">
            {shown.length} событий
            {hidden.size > 0 && ' · часть календарей скрыта'}
          </span>
        )}
      </header>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data &&
        [...byDay].map(([day, events]) => (
          <section key={day} className="day">
            <h4 className="day__title">
              {day === today ? 'Сегодня' : 'Завтра'}
              <span className="muted"> · {formatDay(day, tz)}</span>
            </h4>
            {events.length === 0 && <p className="muted small">Событий нет.</p>}
            <ul className="event-list">
              {events.map((e) => (
                <li key={`${e.calendarId}:${e.uid}:${e.start}`} className="event-row">
                  <span className="event-row__time">
                    {e.allDay ? 'весь день' : `${timeOf(e.start)}–${timeOf(e.end)}`}
                  </span>
                  <span className="event-row__title">
                    {e.title || <span className="muted">без названия</span>}
                    {e.location && <span className="muted"> · {e.location}</span>}
                  </span>
                  <span className="event-row__meta muted small">
                    {e.calendarName}
                    {e.recurring && (
                      <span title="Повторяющееся событие: часть серии в календаре">
                        {' · ↻ регулярное'}
                      </span>
                    )}
                    {e.isAgentOwned && <span title="Создано агентом">{' · создал агент'}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      {query.data?.warnings.map((w) => (
        <p key={w} className="muted small">
          ⚠ {w}
        </p>
      ))}
    </article>
  );
}
